import React from 'react';
import RedirectForbidden from '@app/permissions/RedirectForbidden';
import { useCommHubTextingGate } from '@api/hooks/useCommHubTextingGate';

interface RequireCommHubTextingGateProps {
  children: React.ReactNode;
  shouldRedirectIfDisabled?: boolean;
}

/**
 * Render child components only if the CommHub texting tenant gate
 * (`commHubTextingGates`, contactcenter-api) resolves enabled (CCPE-1710).
 *
 * Mirrors RequireFeature's shape: while the underlying query is still
 * loading, render nothing rather than redirecting — a cold load or refresh
 * of a route wrapped in this gate must not permanently bounce an authorized
 * user to /forbidden before the query resolves. Once loading settles,
 * useCommHubTextingGate is fail-closed (see its docs), so a resolved
 * `isEnabled: false` (whether from an error, an empty tenant list, or a
 * genuinely disabled gate) is treated as disabled here.
 */
const RequireCommHubTextingGate: React.FC<RequireCommHubTextingGateProps> = ({
  children,
  shouldRedirectIfDisabled = false,
}) => {
  const { isEnabled, loading } = useCommHubTextingGate();

  if (loading) {
    return null;
  }

  if (!isEnabled) {
    if (shouldRedirectIfDisabled) {
      return <RedirectForbidden />;
    }

    return null;
  }

  return <>{children}</>;
};

export default RequireCommHubTextingGate;
